import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Tag, Briefcase, Target, Sparkles, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIERS = [
  { key: 'monthly', name: 'Monthly', price: '$97', period: '/month', features: ['Full Network Access', 'Power Guide AI', 'Power Circles', 'Community', 'Power Vault'] },
  { key: 'annual', name: 'Annual', price: '$33', period: '/month', annualNote: 'Billed at $396/year', badge: 'Best Value — Save 66%', features: ['Everything in Monthly', 'Save 66%', 'Priority Support', 'Exclusive Events', 'Founding Badge'] },
  { key: 'premium', name: 'Premium', price: '$297', period: '/month', badge: 'Elite', features: ['Everything in Annual', 'Live Masterminds', '1-on-1 Strategy Calls', 'VIP Inner Circle', 'Founder Access'] },
];

const INDUSTRIES = ['Technology', 'Consulting', 'Finance', 'Healthcare', 'Education', 'Media', 'Marketing', 'Real Estate', 'Design', 'Coaching', 'E-Commerce', 'Other'];

export default function Apply() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    full_name: '', email: '', password: '', current_role: '',
    industry: '', goals: '', why_pivot: '', selected_tier: 'annual', discount_code: ''
  });
  const [discountValid, setDiscountValid] = useState(null);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const validateDiscount = async () => {
    if (!form.discount_code) return;
    try {
      const res = await axios.post(`${API}/discounts/validate`, { code: form.discount_code });
      setDiscountValid(res.data);
      toast.success(`${res.data.discount_percent}% discount applied!`);
    } catch (e) {
      setDiscountValid(null);
      toast.error('Invalid discount code');
    }
  };

  const handleSubmit = async () => {
    if (!form.full_name || !form.email || !form.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      // Save application data for reference
      await axios.post(`${API}/applications/submit`, form).catch(() => {});
      // Create Stripe checkout session
      const res = await axios.post(`${API}/payments/create-checkout`, {
        plan_type: form.selected_tier,
        discount_code: form.discount_code || null,
        email: form.email,
        full_name: form.full_name
      });
      // Redirect to Stripe
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-black via-purple-950 to-black flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg w-full text-center">
          <Loader2 className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-6" />
          <h1 className="text-3xl font-cormorant font-bold text-white mb-4">Redirecting to Payment...</h1>
          <p className="text-gray-400 font-inter text-sm">You'll be redirected to our secure Stripe checkout.</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-purple-950 to-black py-10 px-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <img src="https://customer-assets.emergentagent.com/job_clarity-network-1/artifacts/b1jdxi6j_image.png"
              alt="P2P" className="w-10 h-10 rounded-full" />
            <span className="text-2xl font-cormorant font-bold text-white">Pivot To Power</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-cormorant font-bold bg-gradient-to-r from-amber-300 to-pink-300 bg-clip-text text-transparent mb-2" data-testid="apply-title">
            Apply for Membership
          </h1>
          <p className="text-gray-400 font-inter text-sm max-w-lg mx-auto">
            Pivot To Power is the most transformative reinvention experience on the internet. 
            Membership is selective — we only accept applicants who are serious about building something extraordinary.
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center space-x-2 mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className={`h-1.5 rounded-full transition-all ${s <= step ? 'w-16 bg-gradient-to-r from-amber-400 to-pink-500' : 'w-8 bg-white/10'}`} />
          ))}
        </div>

        <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.1] p-8">
          {/* Step 1: About You */}
          {step === 1 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
              <div className="flex items-center space-x-2 mb-6">
                <Briefcase className="w-5 h-5 text-amber-400" />
                <h2 className="text-xl font-cormorant font-bold text-white">About You</h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm font-inter font-medium mb-1">Full Name *</label>
                  <input value={form.full_name} onChange={e => update('full_name', e.target.value)}
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-3 text-white font-inter text-sm outline-none focus:border-purple-500/50"
                    placeholder="Your full name" data-testid="apply-name" />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm font-inter font-medium mb-1">Email *</label>
                  <input type="email" value={form.email} onChange={e => update('email', e.target.value)}
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-3 text-white font-inter text-sm outline-none focus:border-purple-500/50"
                    placeholder="your@email.com" data-testid="apply-email" />
                </div>
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-inter font-medium mb-1">Create Password *</label>
                <input type="password" value={form.password} onChange={e => update('password', e.target.value)}
                  className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-3 text-white font-inter text-sm outline-none focus:border-purple-500/50"
                  placeholder="Choose a secure password" data-testid="apply-password" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm font-inter font-medium mb-1">Current Role</label>
                  <input value={form.current_role} onChange={e => update('current_role', e.target.value)}
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-3 text-white font-inter text-sm outline-none focus:border-purple-500/50"
                    placeholder="e.g. Marketing Director" data-testid="apply-role" />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm font-inter font-medium mb-1">Industry</label>
                  <select value={form.industry} onChange={e => update('industry', e.target.value)}
                    className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-3 text-white font-inter text-sm outline-none focus:border-purple-500/50"
                    data-testid="apply-industry">
                    <option value="" className="bg-gray-900">Select industry</option>
                    {INDUSTRIES.map(i => <option key={i} value={i} className="bg-gray-900">{i}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex justify-end pt-4">
                <motion.button onClick={() => { if (form.full_name && form.email && form.password) setStep(2); else toast.error('Fill required fields'); }}
                  whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  className="bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold px-8 py-3 rounded-full text-sm"
                  data-testid="apply-next-1">
                  Continue <ArrowRight className="w-4 h-4 inline ml-1" />
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Your Vision */}
          {step === 2 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
              <div className="flex items-center space-x-2 mb-6">
                <Target className="w-5 h-5 text-pink-400" />
                <h2 className="text-xl font-cormorant font-bold text-white">Your Vision</h2>
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-inter font-medium mb-1">What are you looking to build or achieve?</label>
                <textarea value={form.goals} onChange={e => update('goals', e.target.value)}
                  className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-3 text-white font-inter text-sm outline-none focus:border-purple-500/50 min-h-[100px] resize-none"
                  placeholder="A new business, career pivot, side hustle, consulting practice..." data-testid="apply-goals" />
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-inter font-medium mb-1">Why Pivot To Power?</label>
                <textarea value={form.why_pivot} onChange={e => update('why_pivot', e.target.value)}
                  className="w-full bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-3 text-white font-inter text-sm outline-none focus:border-purple-500/50 min-h-[100px] resize-none"
                  placeholder="What draws you to this community? What do you hope to gain?" data-testid="apply-why" />
              </div>
              <div className="flex justify-between pt-4">
                <button onClick={() => setStep(1)} className="text-[#A39BB0] hover:text-white font-inter text-sm">Back</button>
                <motion.button onClick={() => setStep(3)}
                  whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  className="bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold px-8 py-3 rounded-full text-sm"
                  data-testid="apply-next-2">
                  Continue <ArrowRight className="w-4 h-4 inline ml-1" />
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Select Tier */}
          {step === 3 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
              <div className="flex items-center space-x-2 mb-6">
                <Sparkles className="w-5 h-5 text-amber-400" />
                <h2 className="text-xl font-cormorant font-bold text-white">Select Your Tier</h2>
              </div>
              <div className="space-y-3">
                {TIERS.map(tier => (
                  <div key={tier.key} onClick={() => update('selected_tier', tier.key)}
                    className={`p-5 rounded-xl border cursor-pointer transition-all ${
                      form.selected_tier === tier.key
                        ? 'border-pink-500 bg-pink-500/10 shadow-lg shadow-pink-500/10'
                        : 'border-white/[0.1] bg-white/[0.03] hover:border-white/20'
                    }`} data-testid={`apply-tier-${tier.key}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="text-white font-inter font-semibold">{tier.name}</h3>
                          {tier.badge && <span className="bg-gradient-to-r from-amber-400 to-pink-500 text-black text-[10px] font-inter font-bold px-2 py-0.5 rounded-full">{tier.badge}</span>}
                        </div>
                        <p className="text-[#A39BB0] text-xs font-inter mt-1">{tier.features.slice(0, 3).join(' · ')}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-cormorant font-bold text-amber-400">{tier.price}</span>
                        <span className="text-gray-400 text-xs font-inter">{tier.period}</span>
                        {tier.annualNote && <p className="text-gray-500 text-[10px] font-inter mt-0.5">{tier.annualNote}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Discount Code */}
              <div className="pt-2">
                <label className="block text-gray-300 text-sm font-inter font-medium mb-1 flex items-center space-x-1">
                  <Tag className="w-3.5 h-3.5 text-amber-400" /><span>Discount Code (optional)</span>
                </label>
                <div className="flex space-x-2">
                  <input value={form.discount_code} onChange={e => update('discount_code', e.target.value.toUpperCase())}
                    className="flex-1 bg-white/[0.05] border border-white/[0.1] rounded-xl px-4 py-2.5 text-white font-inter text-sm outline-none focus:border-purple-500/50"
                    placeholder="FOUNDING50" data-testid="apply-discount" />
                  <button onClick={validateDiscount}
                    className="bg-amber-500/20 text-amber-400 font-inter font-bold text-sm px-4 py-2.5 rounded-xl border border-amber-500/30 hover:bg-amber-500/30"
                    data-testid="apply-discount-btn">Apply</button>
                </div>
                {discountValid && (
                  <p className="text-emerald-400 text-xs font-inter mt-2">{discountValid.discount_percent}% discount - {discountValid.description}</p>
                )}
              </div>

              <div className="flex justify-between pt-4">
                <button onClick={() => setStep(2)} className="text-[#A39BB0] hover:text-white font-inter text-sm">Back</button>
                <motion.button onClick={handleSubmit} disabled={loading}
                  whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                  className="bg-gradient-to-r from-amber-400 to-pink-500 text-black font-inter font-bold px-10 py-3 rounded-full text-sm shadow-lg shadow-pink-500/30 disabled:opacity-50"
                  data-testid="apply-submit">
                  {loading ? 'Redirecting to Stripe...' : 'Proceed to Payment'}
                </motion.button>
              </div>
            </motion.div>
          )}
        </div>

        <p className="text-center text-gray-500 text-sm font-inter mt-6">
          Already a member? <button onClick={() => navigate('/login')} className="text-amber-400 hover:text-amber-300 font-medium" data-testid="apply-login-link">Login here</button>
        </p>
      </div>
    </div>
  );
}
